import React, { useMemo } from 'react';
import { ShoppingCart, CheckCircle, AlertCircle } from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

/**
 * OrderSummaryBar — Barra flutuante fixa no rodapé mostrando:
 *  - Nº de itens
 *  - Subtotal em tempo real
 *  - Progresso até atingir o valor mínimo (barra + label "Faltam R$ X")
 *  - CTA "Ver Carrinho" ou "Finalizar Pedido"
 *
 * Props:
 *  - itemsCount: number
 *  - total: number
 *  - valorMinimo: number (0 = sem mínimo)
 *  - onOpenCart: () => void
 *  - onCheckout: () => void (usado quando já pode finalizar)
 *  - color: 'blue' | 'emerald' (paleta do portal)
 *  - testidPrefix: string
 */
export default function OrderSummaryBar({
  itemsCount = 0,
  total = 0,
  valorMinimo = 0,
  onOpenCart,
  color = 'blue',
  testidPrefix = 'order-summary',
}) {
  const { faltando, atingiu, percent } = useMemo(() => {
    const min = Number(valorMinimo) || 0;
    if (min <= 0) return { faltando: 0, atingiu: true, percent: 100 };
    const falta = Math.max(0, min - total);
    return {
      faltando: falta,
      atingiu: falta <= 0.005,
      percent: Math.min(100, Math.round((total / min) * 100)),
    };
  }, [total, valorMinimo]);

  if (itemsCount === 0) return null;

  const paletteBg = color === 'emerald' ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-indigo-600';
  const paletteBarBg = color === 'emerald' ? 'bg-emerald-500' : 'bg-blue-500';

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-30 pointer-events-none px-3 pb-3 sm:pb-4"
      data-testid={`${testidPrefix}-bar`}
    >
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Progress bar (só aparece quando existe mínimo) */}
          {valorMinimo > 0 && (
            <div className="h-1.5 bg-gray-100 dark:bg-gray-700">
              <div
                className={`${atingiu ? 'bg-emerald-500' : paletteBarBg} h-full transition-all duration-500`}
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
          <div className="flex items-center gap-3 p-3">
            <div className={`bg-gradient-to-br ${paletteBg} w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
              <ShoppingCart className="w-5 h-5 text-white" />
              <span className="absolute -mt-6 ml-6 bg-orange-500 text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center" data-testid={`${testidPrefix}-count`}>
                {itemsCount}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {itemsCount} {itemsCount === 1 ? 'item' : 'itens'}
                </span>
                <span className="text-lg font-black text-gray-900 dark:text-white tabular-nums" data-testid={`${testidPrefix}-total`}>
                  {fmtBRL(total)}
                </span>
              </div>
              {valorMinimo > 0 && !atingiu && (
                <p className="text-[11px] text-orange-600 dark:text-orange-400 font-semibold flex items-center gap-1 leading-tight" data-testid={`${testidPrefix}-falta`}>
                  <AlertCircle className="w-3 h-3" />
                  Faltam <b>{fmtBRL(faltando)}</b> para atingir o pedido mínimo de <b>{fmtBRL(valorMinimo)}</b>
                </p>
              )}
              {valorMinimo > 0 && atingiu && (
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1 leading-tight" data-testid={`${testidPrefix}-ok`}>
                  <CheckCircle className="w-3 h-3" />
                  Pedido mínimo atingido! Pronto para finalizar.
                </p>
              )}
              {valorMinimo <= 0 && (
                <p className="text-[11px] text-gray-500 leading-tight">
                  Sem valor mínimo configurado
                </p>
              )}
            </div>
            <button
              onClick={onOpenCart}
              className={`bg-gradient-to-r ${paletteBg} text-white px-4 py-2.5 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all whitespace-nowrap`}
              data-testid={`${testidPrefix}-cta`}
            >
              Ver Carrinho
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * CategoryChips — barra rolável horizontal de categorias.
 *
 * Props:
 *  - categorias: string[] (nomes únicos)
 *  - selected: string ('' significa "Todos")
 *  - onSelect: (categoria: string) => void
 *  - color: 'blue' | 'emerald'
 *  - testidPrefix: string
 */
export function CategoryChips({ categorias = [], selected = '', onSelect, color = 'blue', testidPrefix = 'category' }) {
  if (categorias.length === 0) return null;
  const activeBg = color === 'emerald' ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white';
  return (
    <div className="overflow-x-auto -mx-2 px-2 pb-1" data-testid={`${testidPrefix}-chips`}>
      <div className="flex items-center gap-2 min-w-max">
        <button
          onClick={() => onSelect('')}
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
            selected === '' ? activeBg : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300'
          }`}
          data-testid={`${testidPrefix}-chip-todos`}
        >
          Todos
        </button>
        {categorias.map(cat => (
          <button
            key={cat}
            onClick={() => onSelect(cat)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              selected === cat ? activeBg : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
            data-testid={`${testidPrefix}-chip-${cat.toLowerCase().replace(/\s/g, '-')}`}
          >
            {cat}
          </button>
        ))}
      </div>
    </div>
  );
}
